import { ContactValue, DetailSection } from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import type { AdminOrganizationMember } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import {
  useOrgMemberRoleLabel,
  useOrgMemberStatusLabel,
  useOrgPermissionLabel,
} from '@/lib/labels';
import { memberTone } from '@/lib/status';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';

/** Owners and HR managers hold every permission; the roster says so once, not eight times. */
const ALL_PERMISSION_ROLES = new Set(['owner', 'hr_manager']);

export function MembersSection({ members }: { members: readonly AdminOrganizationMember[] }) {
  const t = useTranslations('admin.organizationDetail');
  const common = useTranslations('common');
  const roleLabel = useOrgMemberRoleLabel();
  const statusLabel = useOrgMemberStatusLabel();
  const permissionLabel = useOrgPermissionLabel();

  const columns: ReadonlyArray<DataTableColumn<AdminOrganizationMember>> = [
    {
      key: 'name',
      header: t('members.name'),
      cellClassName: 'font-semibold text-navy',
      cell: (member) => <span dir="auto">{member.fullName}</span>,
    },
    {
      key: 'role',
      header: t('members.role'),
      cellClassName: 'text-conc-600',
      cell: (member) => (
        <>
          <p>{member.role ? roleLabel(member.role) : '—'}</p>
          {member.department && (
            <p className="text-[11.5px] text-conc-500" dir="auto">
              {member.department}
            </p>
          )}
        </>
      ),
    },
    {
      key: 'status',
      header: t('members.status'),
      cell: (member) =>
        member.status ? (
          <StatusBadge tone={memberTone(member.status)}>{statusLabel(member.status)}</StatusBadge>
        ) : (
          '—'
        ),
    },
    {
      key: 'contact',
      header: t('members.contact'),
      cellClassName: 'text-conc-600',
      cell: (member) => (
        <>
          {member.phone && <ContactValue value={member.phone} copyLabel={t('copyPhone')} numeric />}
          {member.email && <ContactValue value={member.email} copyLabel={common('copyEmail')} />}
          {!member.phone && !member.email && '—'}
        </>
      ),
    },
    {
      key: 'permissions',
      header: t('members.permissions'),
      cell: (member) =>
        member.role && ALL_PERMISSION_ROLES.has(member.role) ? (
          <span className="rounded-md bg-pitch-50 px-1.5 py-0.5 text-[11px] font-semibold text-pitch-700">
            {t('members.allPermissions')}
          </span>
        ) : member.permissions && member.permissions.length > 0 ? (
          <ul className="flex flex-wrap gap-1" aria-label={t('members.permissions')}>
            {member.permissions.map((permission) => (
              <li
                key={permission}
                title={permission}
                className="rounded-md bg-conc-50 px-1.5 py-0.5 text-[11px] text-conc-600"
              >
                {permissionLabel(permission)}
              </li>
            ))}
          </ul>
        ) : (
          '—'
        ),
    },
    {
      key: 'dates',
      header: t('members.dates'),
      cellClassName: 'num text-[11.5px] text-conc-500',
      cell: (member) => (
        <>
          {member.invitedAt && (
            <p>{t('members.invited', { date: isoDate(member.invitedAt) ?? '' })}</p>
          )}
          {member.joinedAt && (
            <p>{t('members.joined', { date: isoDate(member.joinedAt) ?? '' })}</p>
          )}
          {!member.invitedAt && !member.joinedAt && '—'}
        </>
      ),
    },
  ];

  return (
    <DetailSection title={t('section.members')}>
      {members.length === 0 ? (
        <p className="text-[12.5px] text-conc-400">{t('members.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            inline
            caption={t('section.members')}
            rows={members}
            columns={columns}
            rowKey={(member) => member.id}
          />
        </div>
      )}
    </DetailSection>
  );
}
